/**
 * Attorney Case Detail - View released case with analysis sections
 * All tools embedded inline: Settlement Intelligence, Advanced Tools, Demand Letter
 * Sidebar scroll-to-section navigation targets section IDs on this page
 * Uses AttorneyLayout for consistent look with main app
 */

import React, { Suspense, useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Scale,
  FileText,
  Download,
  ArrowLeft,
  Calendar,
  Loader2,
  AlertCircle,
  Target,
  Shield,
  BarChart3,
  ChevronDown,
  ChevronUp,
  Clock,
  DollarSign,
  FileCheck,
  AlertTriangle,
  Info,
  Upload,
  ExternalLink
} from 'lucide-react';
import AttorneyLayout from '../components/AttorneyLayout';
import CaseTimeline, { generateTimelineEvents } from '../components/CaseTimeline';

// Lazy-loaded tool components
const SettlementIntelligenceDashboard = React.lazy(() => import('../components/analytics/SettlementIntelligenceDashboard'));
const GameTheoryDashboard = React.lazy(() => import('../components/analytics/GameTheoryDashboard'));
const BadFaithTracker = React.lazy(() => import('../components/analytics/BadFaithTracker'));
const CaseLawSearch = React.lazy(() => import('../components/analytics/CaseLawSearch'));
const EvidenceChecklist = React.lazy(() => import('../components/EvidenceChecklist'));
const DemandLetterGenerator = React.lazy(() => import('./DemandLetterGenerator'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface PolicyCoverage {
  claim_type: 'first_party' | 'third_party' | 'unknown';
  responsible_party?: string;
  bi_limit?: number;
  extra_expense_limit?: number;
  combined_limit?: number;
  deductible?: number;
  waiting_period_days?: number;
  exclusions?: string[];
  policy_document_url?: string;
  policy_number?: string;
  insurer_name?: string;
  effective_date?: string;
  expiration_date?: string;
}

interface CaseDetail {
  id: string;
  client_name: string;
  business_name?: string;
  event_type?: string;
  event_date?: string;
  total_demand?: number;
  released_at: string;
  calculation_mode?: string;
  attorney_analysis?: any;
  adjuster_analysis?: any;
  fair_settlement?: any;
  pro_forma_summary?: any;
  monte_carlo_results?: any;
  policy_coverage?: PolicyCoverage;
  // Timeline fields
  created_at?: string;
  created_by_name?: string;
  released_by_name?: string;
  submitted_at?: string;
  submitted_by_name?: string;
  updated_at?: string;
  last_downloaded_at?: string;
  last_download_format?: string;
  last_downloaded_by?: string;
  last_viewed_at?: string;
}

export default function AttorneyCaseDetail() {
  const { caseId } = useParams<{ caseId: string }>();
  const [caseData, setCaseData] = useState<CaseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState<'pdf' | 'docx' | null>(null);
  const [activeSection, setActiveSection] = useState<string>('');
  
  // Section refs for scrolling
  const policyCoverageRef = useRef<HTMLDivElement>(null);
  const attorneyAnalysisRef = useRef<HTMLDivElement>(null);
  const adjusterAnalysisRef = useRef<HTMLDivElement>(null);
  const fairSettlementRef = useRef<HTMLDivElement>(null);
  const monteCarloRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  
  // Section collapse states
  const [showPolicyCoverage, setShowPolicyCoverage] = useState(false); // Collapsed by default
  const [showAttorneyAnalysis, setShowAttorneyAnalysis] = useState(true);
  const [showAdjusterAnalysis, setShowAdjusterAnalysis] = useState(true);
  const [showFairSettlement, setShowFairSettlement] = useState(true);
  const [showProForma, setShowProForma] = useState(true);
  const [showTimeline, setShowTimeline] = useState(true);
  
  // Policy coverage state
  const [claimType, setClaimType] = useState<'first_party' | 'third_party' | 'unknown'>('unknown');
  const [updatingClaimType, setUpdatingClaimType] = useState(false);

  // Settlement dashboard tab state (controls which tab is shown when scrolling to settlement sections)
  const [settlementTab, setSettlementTab] = useState<'sensitivity' | 'historical' | 'whatif' | 'builder' | 'litigation'>('sensitivity');
  
  const navigate = useNavigate();
  const location = useLocation();

  // Map sidebar section IDs to SettlementIntelligenceDashboard tab names
  const settlementTabMap: Record<string, 'sensitivity' | 'historical' | 'whatif' | 'builder' | 'litigation'> = {
    'sensitivity': 'sensitivity',
    'historical': 'historical',
    'what-if': 'whatif',
    'strategy-builder': 'builder',
    'litigation-econ': 'litigation',
  };

  // Handle section change from sidebar
  const handleSectionChange = (section: string) => {
    setActiveSection(section);

    // Handle downloads
    if (section === 'download-pdf') {
      handleDownload('pdf');
      return;
    }
    if (section === 'download-docx') {
      handleDownload('docx');
      return;
    }

    // If a settlement sub-tab is selected, switch the dashboard tab and scroll to the settlement section
    if (settlementTabMap[section]) {
      setSettlementTab(settlementTabMap[section]);
      const el = document.getElementById('sensitivity');
      if (el) {
        const elementPosition = el.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - 100;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }
      return;
    }

    // Scroll to section and expand it
    let ref: React.RefObject<HTMLDivElement> | null = null;

    switch (section) {
      case 'policy-coverage':
        ref = policyCoverageRef;
        setShowPolicyCoverage(true);
        break;
      case 'attorney-analysis':
        ref = attorneyAnalysisRef;
        setShowAttorneyAnalysis(true);
        break;
      case 'adjuster-analysis':
        ref = adjusterAnalysisRef;
        setShowAdjusterAnalysis(true);
        break;
      case 'fair-settlement':
        ref = fairSettlementRef;
        setShowFairSettlement(true);
        break;
      case 'monte-carlo':
        ref = monteCarloRef;
        break;
      case 'timeline':
        ref = timelineRef;
        setShowTimeline(true);
        break;
    }

    if (ref?.current) {
      // Get element position and scroll with offset for header
      const elementPosition = ref.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - 100; // 100px offset for header

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    } else {
      // For tool sections that use id-based elements (not refs)
      const el = document.getElementById(section);
      if (el) {
        const elementPosition = el.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - 100;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('attorney_token');
    if (!token) {
      navigate('/attorney/login');
      return;
    }
    fetchCaseDetail();
  }, [caseId]);

  // Check if we need to scroll to a section (when navigating back from sub-pages)
  useEffect(() => {
    const scrollToSection = sessionStorage.getItem('scrollToSection');
    if (scrollToSection && caseData) {
      sessionStorage.removeItem('scrollToSection');
      // Trigger section change after a brief delay for DOM to be ready
      setTimeout(() => {
        handleSectionChange(scrollToSection);
      }, 100);
    }
  }, [caseData]);

  // Handle URL hash-based navigation (e.g., /attorney/case/123#sensitivity)
  useEffect(() => {
    if (caseData && location.hash) {
      const sectionId = location.hash.replace('#', '');
      if (sectionId) {
        // Delay to allow lazy-loaded components to mount
        setTimeout(() => {
          handleSectionChange(sectionId);
        }, 300);
      }
    }
  }, [caseData, location.hash]);

  const fetchCaseDetail = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('attorney_token');
      const response = await fetch(`${BACKEND_URL}/api/v1/attorney/case/${caseId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        localStorage.removeItem('attorney_token');
        localStorage.removeItem('attorney_profile');
        navigate('/attorney/login');
        return;
      }

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to fetch case');
      }

      const data = await response.json();
      setCaseData(data);
      // Set claim type from fetched data
      if (data.policy_coverage?.claim_type) {
        setClaimType(data.policy_coverage.claim_type);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load case');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (format: 'pdf' | 'docx') => {
    setDownloading(format);

    try {
      const token = localStorage.getItem('attorney_token');
      const response = await fetch(
        `${BACKEND_URL}/api/v1/attorney/case/${caseId}/download/${format}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (!response.ok) {
        throw new Error('Download failed');
      }

      // Get filename from header or create default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `Report.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }

      // Download file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      toast.error(`Download failed: ${err.message}`);
    } finally {
      setDownloading(null);
    }
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Update claim type on the server
  const handleClaimTypeChange = async (newType: 'first_party' | 'third_party') => {
    setUpdatingClaimType(true);
    try {
      const token = localStorage.getItem('attorney_token');
      const response = await fetch(`${BACKEND_URL}/api/v1/attorney/case/${caseId}/claim-type`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ claim_type: newType })
      });
      
      if (response.ok) {
        setClaimType(newType);
        // Update local state
        if (caseData) {
          setCaseData({
            ...caseData,
            policy_coverage: {
              ...caseData.policy_coverage,
              claim_type: newType
            }
          });
        }
      }
    } catch (err) {
      console.error('Failed to update claim type:', err);
    } finally {
      setUpdatingClaimType(false);
    }
  };

  // Download policy document
  const handleDownloadPolicy = async () => {
    if (!caseData?.policy_coverage?.policy_document_url) return;
    
    try {
      const token = localStorage.getItem('attorney_token');
      const response = await fetch(`${BACKEND_URL}/api/v1/attorney/case/${caseId}/policy-document`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Policy_${caseData.client_name || 'document'}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Failed to download policy:', err);
    }
  };

  // Check if calculated demand exceeds policy limits
  const getPolicyWarning = () => {
    if (!caseData?.policy_coverage || claimType !== 'first_party') return null;
    
    const policy = caseData.policy_coverage;
    const demand = caseData.total_demand || 0;
    const limit = policy.combined_limit || policy.bi_limit || 0;
    
    if (limit > 0 && demand > limit) {
      const excess = demand - limit;
      return {
        type: 'warning' as const,
        message: `Calculated loss (${formatCurrency(demand)}) exceeds policy limit (${formatCurrency(limit)}) by ${formatCurrency(excess)}`
      };
    }
    return null;
  };

  if (loading) {
    return (
      <AttorneyLayout activeSection={activeSection} onSectionChange={handleSectionChange}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-slate-600">Loading case details...</p>
          </div>
        </div>
      </AttorneyLayout>
    );
  }

  if (error || !caseData) {
    return (
      <AttorneyLayout activeSection={activeSection} onSectionChange={handleSectionChange}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">Error Loading Case</h2>
          <p className="text-slate-600 mb-6">{error || 'Case not found'}</p>
          <Link
            to="/attorney/dashboard"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
        </div>
        </div>
      </AttorneyLayout>
    );
  }

  return (
    <AttorneyLayout activeSection={activeSection} onSectionChange={handleSectionChange}>
      {/* Back button and Download Buttons */}
      <div className="flex items-center justify-between mb-6">
        <Link
          to="/attorney/dashboard"
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Dashboard</span>
        </Link>

        {/* Download Buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleDownload('pdf')}
            disabled={downloading !== null}
            className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
            data-testid="download-pdf-button"
          >
            {downloading === 'pdf' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            PDF
          </button>
          <button
            onClick={() => handleDownload('docx')}
            disabled={downloading !== null}
            className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50"
            data-testid="download-docx-button"
          >
            {downloading === 'docx' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Word
          </button>
        </div>
      </div>

      {/* Case Header */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{caseData.client_name}</h1>
            {caseData.business_name && (
              <p className="text-slate-600 mt-1">{caseData.business_name}</p>
            )}
            <div className="flex flex-wrap items-center gap-3 mt-3">
              {caseData.event_type && (
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                  {caseData.event_type}
                </span>
              )}
              {caseData.calculation_mode && (
                <span className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm">
                  {caseData.calculation_mode === 'future_closure' ? 'Future Closure' : 'Standard BI'}
                </span>
              )}
              <span className="flex items-center gap-1 text-sm text-slate-500">
                <Calendar className="w-4 h-4" />
                Released {formatDate(caseData.released_at)}
              </span>
            </div>
          </div>

          {/* Total Demand */}
          <div className="bg-green-50 rounded-xl p-4 text-center md:text-right">
            <p className="text-sm text-green-700 font-medium">Total Demand</p>
            <p className="text-3xl font-bold text-green-600">
                {formatCurrency(caseData.total_demand)}
              </p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-3 mb-6">
          <button
            onClick={() => handleSectionChange('demand-letter')}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            data-testid="btn-demand-letter"
          >
            <FileText className="w-4 h-4" />
            Demand Letter
          </button>
          <Link
            to={`/attorney/case/${caseId}/timeline`}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            data-testid="btn-bi-timeline"
          >
            <Calendar className="w-4 h-4" />
            BI Timeline
          </Link>
          <button
            onClick={() => handleSectionChange('bad-faith')}
            className="flex items-center gap-2 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
            data-testid="btn-advanced-tools"
          >
            <Scale className="w-4 h-4" />
            Advanced Tools
          </button>
          <button
            onClick={() => handleSectionChange('sensitivity')}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            data-testid="btn-settlement-tools"
          >
            <DollarSign className="w-4 h-4" />
            Settlement Tools
          </button>
        </div>

        {/* Policy Coverage Section - Collapsible */}
        <div id="overview" ref={policyCoverageRef} className={`bg-white rounded-xl border mb-6 overflow-hidden transition-all ${activeSection === 'policy-coverage' ? 'border-blue-500 ring-2 ring-blue-200' : 'border-slate-200'}`} data-testid="policy-coverage-section">
          <button
            onClick={() => setShowPolicyCoverage(!showPolicyCoverage)}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
            data-testid="policy-coverage-toggle"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <FileCheck className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="text-left">
                <h2 className="text-lg font-semibold text-slate-900">Policy Coverage</h2>
                <p className="text-sm text-slate-500">
                  {claimType === 'first_party' ? 'First-party claim' : 
                   claimType === 'third_party' ? 'Third-party claim' : 'Claim type not set'}
                </p>
              </div>
            </div>
            {showPolicyCoverage ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
          </button>
          
          {showPolicyCoverage && (
            <div className="px-6 pb-6 border-t border-slate-100">
              <div className="pt-4 space-y-4">
                {/* Claim Type Selection */}
                <div className="flex items-center gap-4 mb-4">
                  <span className="text-sm font-medium text-slate-700">Claim Type:</span>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="claim_type"
                        checked={claimType === 'first_party'}
                        onChange={() => handleClaimTypeChange('first_party')}
                        disabled={updatingClaimType}
                        className="w-4 h-4 text-indigo-600"
                        data-testid="claim-type-first-party"
                      />
                      <span className="text-sm text-slate-600">First-Party</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="claim_type"
                        checked={claimType === 'third_party'}
                        onChange={() => handleClaimTypeChange('third_party')}
                        disabled={updatingClaimType}
                        className="w-4 h-4 text-indigo-600"
                        data-testid="claim-type-third-party"
                      />
                      <span className="text-sm text-slate-600">Third-Party</span>
                    </label>
                    {updatingClaimType && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                  </div>
                </div>

                {/* First-Party Content */}
                {claimType === 'first_party' && (
                  <>
                    {caseData?.policy_coverage?.bi_limit || caseData?.policy_coverage?.combined_limit ? (
                      <>
                        {/* Policy Limits Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {caseData.policy_coverage.bi_limit && (
                            <div className="bg-slate-50 rounded-lg p-4">
                              <p className="text-sm text-slate-500">BI Limit</p>
                              <p className="text-lg font-bold text-slate-900">
                                {formatCurrency(caseData.policy_coverage.bi_limit)}
                              </p>
                            </div>
                          )}
                          {caseData.policy_coverage.extra_expense_limit && (
                            <div className="bg-slate-50 rounded-lg p-4">
                              <p className="text-sm text-slate-500">Extra Expense</p>
                              <p className="text-lg font-bold text-slate-900">
                                {formatCurrency(caseData.policy_coverage.extra_expense_limit)}
                              </p>
                            </div>
                          )}
                          {caseData.policy_coverage.deductible && (
                            <div className="bg-slate-50 rounded-lg p-4">
                              <p className="text-sm text-slate-500">Deductible</p>
                              <p className="text-lg font-bold text-slate-900">
                                {formatCurrency(caseData.policy_coverage.deductible)}
                              </p>
                            </div>
                          )}
                          {caseData.policy_coverage.waiting_period_days && (
                            <div className="bg-slate-50 rounded-lg p-4">
                              <p className="text-sm text-slate-500">Waiting Period</p>
                              <p className="text-lg font-bold text-slate-900">
                                {caseData.policy_coverage.waiting_period_days} days
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Policy Warning */}
                        {getPolicyWarning() && (
                          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-medium text-amber-800">Coverage Limit Warning</p>
                              <p className="text-sm text-amber-700 mt-1">{getPolicyWarning()?.message}</p>
                            </div>
                          </div>
                        )}

                        {/* Exclusions */}
                        {caseData.policy_coverage.exclusions && caseData.policy_coverage.exclusions.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-slate-700 mb-2">Key Exclusions</p>
                            <div className="flex flex-wrap gap-2">
                              {caseData.policy_coverage.exclusions.map((exclusion, i) => (
                                <span key={i} className="px-3 py-1 bg-red-50 text-red-700 rounded-full text-sm">
                                  {exclusion}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Policy Document Download */}
                        {caseData.policy_coverage.policy_document_url && (
                          <div className="flex items-center gap-3 pt-2">
                            <button
                              onClick={handleDownloadPolicy}
                              className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors"
                              data-testid="download-policy-btn"
                            >
                              <Download className="w-4 h-4" />
                              View Full Policy
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      /* No Policy on File */
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-medium text-amber-800">No policy on file</p>
                            <p className="text-sm text-amber-700 mt-1">
                              Policy limits not available. Upload the policy document to enable coverage analysis.
                            </p>
                            <p className="text-xs text-amber-600 mt-2">
                              Contact your case administrator to upload the policy via the main upload flow.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Third-Party Content */}
                {claimType === 'third_party' && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-blue-800">Third-party claim</p>
                        <p className="text-sm text-blue-700 mt-1">
                          This is a claim against the responsible party's insurance. 
                          Client's policy limits do not cap recoverable damages.
                        </p>
                        {caseData?.policy_coverage?.responsible_party && (
                          <p className="text-sm text-blue-800 mt-2">
                            <span className="font-medium">Responsible Party:</span> {caseData.policy_coverage.responsible_party}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Unknown Claim Type */}
                {claimType === 'unknown' && (
                  <div className="p-4 bg-slate-100 border border-slate-200 rounded-lg">
                    <p className="text-sm text-slate-600">
                      Please select a claim type above to view coverage information.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Pro Forma Summary */}
        {caseData.pro_forma_summary && (
          <div id="pro-forma" className="bg-white rounded-xl border border-slate-200 mb-6 overflow-hidden">
            <button
              onClick={() => setShowProForma(!showProForma)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-lg font-semibold text-slate-900">Pro Forma Summary</h2>
              </div>
              {showProForma ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
            </button>
            
            {showProForma && (
              <div className="px-6 pb-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm text-slate-500">Lost Profits</p>
                    <p className="text-xl font-bold text-slate-900">
                      {formatCurrency(caseData.pro_forma_summary.total_lost_profits)}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm text-slate-500">Continued Expenses</p>
                    <p className="text-xl font-bold text-slate-900">
                      {formatCurrency(caseData.pro_forma_summary.total_continued_expenses)}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-sm text-slate-500">One-Time Costs</p>
                    <p className="text-xl font-bold text-slate-900">
                      {formatCurrency(caseData.pro_forma_summary.one_time_costs)}
                    </p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <p className="text-sm text-green-700">Total Demand</p>
                    <p className="text-xl font-bold text-green-600">
                      {formatCurrency(caseData.pro_forma_summary.total_demand)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Attorney Analysis Section */}
        {caseData.attorney_analysis && (
          <div id="attorney-analysis" ref={attorneyAnalysisRef} className={`bg-white rounded-xl border mb-6 overflow-hidden transition-all ${activeSection === 'attorney-analysis' ? 'border-blue-500 ring-2 ring-blue-200' : 'border-slate-200'}`}>
            <button
              onClick={() => setShowAttorneyAnalysis(!showAttorneyAnalysis)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Scale className="w-5 h-5 text-blue-600" />
                </div>
                <div className="text-left">
                  <h2 className="text-lg font-semibold text-slate-900">Attorney Analysis</h2>
                  <p className="text-sm text-slate-500">Key arguments and damage breakdown for your case</p>
                </div>
              </div>
              {showAttorneyAnalysis ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
            </button>
            
            {showAttorneyAnalysis && (
              <div className="px-6 pb-6 border-t border-slate-100">
                <div className="pt-4 prose prose-slate max-w-none">
                  {typeof caseData.attorney_analysis === 'string' ? (
                    <p>{caseData.attorney_analysis}</p>
                  ) : (
                    <div className="space-y-4">
                      {caseData.attorney_analysis.summary && (
                        <div>
                          <h4 className="font-medium text-slate-900">Summary</h4>
                          <p className="text-slate-600">{caseData.attorney_analysis.summary}</p>
                        </div>
                      )}
                      {caseData.attorney_analysis.key_arguments && (
                        <div>
                          <h4 className="font-medium text-slate-900">Key Arguments</h4>
                          <ul className="list-disc list-inside text-slate-600">
                            {caseData.attorney_analysis.key_arguments.map((arg: string, i: number) => (
                              <li key={i}>{arg}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {caseData.attorney_analysis.damage_breakdown && (
                        <div>
                          <h4 className="font-medium text-slate-900 mb-3">Damage Breakdown</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {caseData.attorney_analysis.damage_breakdown.lost_profits !== undefined && (
                              <div className="bg-blue-50 rounded-lg p-4">
                                <p className="text-sm text-blue-600 font-medium">Lost Profits</p>
                                <p className="text-xl font-bold text-blue-700">
                                  {formatCurrency(caseData.attorney_analysis.damage_breakdown.lost_profits)}
                                </p>
                              </div>
                            )}
                            {caseData.attorney_analysis.damage_breakdown.continued_expenses !== undefined && (
                              <div className="bg-amber-50 rounded-lg p-4">
                                <p className="text-sm text-amber-600 font-medium">Continued Expenses</p>
                                <p className="text-xl font-bold text-amber-700">
                                  {formatCurrency(caseData.attorney_analysis.damage_breakdown.continued_expenses)}
                                </p>
                              </div>
                            )}
                            {caseData.attorney_analysis.damage_breakdown.extra_expenses !== undefined && (
                              <div className="bg-purple-50 rounded-lg p-4">
                                <p className="text-sm text-purple-600 font-medium">Extra Expenses</p>
                                <p className="text-xl font-bold text-purple-700">
                                  {formatCurrency(caseData.attorney_analysis.damage_breakdown.extra_expenses)}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Adjuster Analysis Section */}
        {caseData.adjuster_analysis && (
          <div id="adjuster-analysis" ref={adjusterAnalysisRef} className={`bg-white rounded-xl border mb-6 overflow-hidden transition-all ${activeSection === 'adjuster-analysis' ? 'border-amber-500 ring-2 ring-amber-200' : 'border-slate-200'}`}>
            <button
              onClick={() => setShowAdjusterAnalysis(!showAdjusterAnalysis)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-amber-600" />
                </div>
                <div className="text-left">
                  <h2 className="text-lg font-semibold text-slate-900">Adjuster Analysis</h2>
                  <p className="text-sm text-slate-500">What the opposing side may argue</p>
                </div>
              </div>
              {showAdjusterAnalysis ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
            </button>
            
            {showAdjusterAnalysis && (
              <div className="px-6 pb-6 border-t border-slate-100">
                <div className="pt-4 prose prose-slate max-w-none">
                  {typeof caseData.adjuster_analysis === 'string' ? (
                    <p>{caseData.adjuster_analysis}</p>
                  ) : (
                    <div className="space-y-4">
                      {caseData.adjuster_analysis.carrier_perspective && (
                        <div>
                          <h4 className="font-medium text-slate-900">Carrier Perspective</h4>
                          <p className="text-slate-600">{caseData.adjuster_analysis.carrier_perspective}</p>
                        </div>
                      )}
                      {caseData.adjuster_analysis.potential_challenges && (
                        <div>
                          <h4 className="font-medium text-slate-900">Potential Challenges</h4>
                          <ul className="list-disc list-inside text-slate-600">
                            {caseData.adjuster_analysis.potential_challenges.map((c: string, i: number) => (
                              <li key={i}>{c}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {caseData.adjuster_analysis.counter_arguments && (
                        <div>
                          <h4 className="font-medium text-slate-900">Counter Arguments</h4>
                          <ul className="list-disc list-inside text-slate-600">
                            {caseData.adjuster_analysis.counter_arguments.map((c: string, i: number) => (
                              <li key={i}>{c}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Fair Settlement Section */}
        {caseData.fair_settlement && (
          <div id="fair-settlement" ref={fairSettlementRef} className={`bg-white rounded-xl border mb-6 overflow-hidden transition-all ${activeSection === 'fair-settlement' ? 'border-green-500 ring-2 ring-green-200' : 'border-slate-200'}`}>
            <button
              onClick={() => setShowFairSettlement(!showFairSettlement)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Target className="w-5 h-5 text-green-600" />
                </div>
                <div className="text-left">
                  <h2 className="text-lg font-semibold text-slate-900">Fair Settlement Analysis</h2>
                  <p className="text-sm text-slate-500">Settlement range and negotiation guidance</p>
                </div>
              </div>
              {showFairSettlement ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
            </button>
            
            {showFairSettlement && (
              <div className="px-6 pb-6 border-t border-slate-100">
                <div className="pt-4">
                  {typeof caseData.fair_settlement === 'string' ? (
                    <p className="text-slate-600">{caseData.fair_settlement}</p>
                  ) : (
                    <div className="space-y-6">
                      {/* Settlement Range */}
                      {(caseData.fair_settlement.low || caseData.fair_settlement.high) && (
                        <div className="bg-slate-50 rounded-lg p-4">
                          <h4 className="font-medium text-slate-900 mb-3">Settlement Range</h4>
                          <div className="grid grid-cols-3 gap-4">
                            <div className="text-center">
                              <p className="text-sm text-slate-500">Low</p>
                              <p className="text-xl font-bold text-red-600">
                                {formatCurrency(caseData.fair_settlement.low)}
                              </p>
                            </div>
                            <div className="text-center">
                              <p className="text-sm text-slate-500">Target</p>
                              <p className="text-xl font-bold text-green-600">
                                {formatCurrency(caseData.fair_settlement.target || caseData.fair_settlement.median)}
                              </p>
                            </div>
                            <div className="text-center">
                              <p className="text-sm text-slate-500">High</p>
                              <p className="text-xl font-bold text-blue-600">
                                {formatCurrency(caseData.fair_settlement.high)}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {caseData.fair_settlement.recommendation && (
                        <div>
                          <h4 className="font-medium text-slate-900">Recommendation</h4>
                          <p className="text-slate-600 mt-1">{caseData.fair_settlement.recommendation}</p>
                        </div>
                      )}
                      
                      {caseData.fair_settlement.negotiation_tips && (
                        <div>
                          <h4 className="font-medium text-slate-900">Negotiation Tips</h4>
                          <ul className="list-disc list-inside text-slate-600 mt-1">
                            {caseData.fair_settlement.negotiation_tips.map((tip: string, i: number) => (
                              <li key={i}>{tip}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Monte Carlo Results */}
        {caseData.monte_carlo_results && (
          <div id="monte-carlo" ref={monteCarloRef} className={`bg-white rounded-xl border mb-6 p-6 transition-all ${activeSection === 'monte-carlo' ? 'border-purple-500 ring-2 ring-purple-200' : 'border-slate-200'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-purple-600" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900">Monte Carlo Analysis</h2>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <p className="text-sm text-slate-500">Conservative (P10)</p>
                <p className="text-xl font-bold text-slate-900">
                  {formatCurrency(caseData.monte_carlo_results.p10)}
                </p>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <p className="text-sm text-green-700">Median (P50)</p>
                <p className="text-xl font-bold text-green-600">
                  {formatCurrency(caseData.monte_carlo_results.p50)}
                </p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <p className="text-sm text-slate-500">Aggressive (P90)</p>
                <p className="text-xl font-bold text-slate-900">
                  {formatCurrency(caseData.monte_carlo_results.p90)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Case Timeline */}
        <div id="timeline" ref={timelineRef} className={`bg-white rounded-xl border mb-6 overflow-hidden transition-all ${activeSection === 'timeline' ? 'border-blue-500 ring-2 ring-blue-200' : 'border-slate-200'}`}>
          <button
            onClick={() => setShowTimeline(!showTimeline)}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-slate-600" />
              </div>
              <div className="text-left">
                <h2 className="text-lg font-semibold text-slate-900">Case Timeline</h2>
                <p className="text-sm text-slate-500">History and milestones for this case</p>
              </div>
            </div>
            {showTimeline ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
          </button>
          
          {showTimeline && (
            <div className="px-6 pb-6 border-t border-slate-100">
              <div className="pt-4">
                <CaseTimeline events={generateTimelineEvents(caseData)} />
              </div>
            </div>
          )}
        </div>

        {/* No Analysis Data Message */}
        {!caseData.attorney_analysis && !caseData.adjuster_analysis && !caseData.fair_settlement && (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center shadow-sm">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">Analysis Sections Not Available</h3>
            <p className="text-slate-500 max-w-md mx-auto">
              The detailed analysis sections for this case are not yet available.
              Please download the report for full case details.
            </p>
          </div>
        )}

        {/* ====== Settlement Tools Sections ====== */}
        <section id="sensitivity" className="mt-8 pt-8 border-t border-slate-200">
          {/* Anchor targets for sidebar IntersectionObserver */}
          <div id="historical" style={{ position: 'relative', top: '-100px' }} />
          <div id="what-if" style={{ position: 'relative', top: '-100px' }} />
          <div id="strategy-builder" style={{ position: 'relative', top: '-100px' }} />
          <div id="litigation-econ" style={{ position: 'relative', top: '-100px' }} />
          <h2 className="text-xl font-bold text-slate-900 mb-1">Settlement Intelligence</h2>
          <p className="text-sm text-slate-500 mb-4">Sensitivity analysis, historical benchmarks, what-if scenarios, strategy builder, and litigation economics</p>
          <Suspense fallback={<div className="animate-pulse bg-slate-100 h-64 rounded-xl" />}>
            <SettlementIntelligenceDashboard
              key={settlementTab}
              initialTab={settlementTab}
              caseId={caseId}
              claimValue={caseData.total_demand}
              embedded={true}
            />
          </Suspense>
        </section>

        {/* Game Theory Section */}
        <section id="game-theory" className="mt-8 pt-8 border-t border-slate-200">
          <h2 className="text-xl font-bold text-slate-900 mb-1">Game Theory Analysis</h2>
          <p className="text-sm text-slate-500 mb-4">Nash equilibrium and negotiation strategy modeling</p>
          <Suspense fallback={<div className="animate-pulse bg-slate-100 h-64 rounded-xl" />}>
            <GameTheoryDashboard initialClaimValue={caseData.total_demand} />
          </Suspense>
        </section>

        {/* ====== Advanced Tools Sections ====== */}
        {/* Bad Faith Tracker Section */}
        <section id="bad-faith" className="mt-8 pt-8 border-t border-slate-200">
          <h2 className="text-xl font-bold text-slate-900 mb-1">Bad Faith Tracker</h2>
          <p className="text-sm text-slate-500 mb-4">Track carrier violations, delays, and bad faith indicators</p>
          <Suspense fallback={<div className="animate-pulse bg-slate-100 h-64 rounded-xl" />}>
            <BadFaithTracker
              caseId={caseId}
              initialClaimAmount={caseData.total_demand || 0}
            />
          </Suspense>
        </section>

        {/* Case Law Search Section */}
        <section id="case-law" className="mt-8 pt-8 border-t border-slate-200">
          <h2 className="text-xl font-bold text-slate-900 mb-1">Case Law Search</h2>
          <p className="text-sm text-slate-500 mb-4">Search comparable cases and precedent analysis</p>
          <Suspense fallback={<div className="animate-pulse bg-slate-100 h-64 rounded-xl" />}>
            <CaseLawSearch
              caseId={caseId}
              initialEventType={caseData.event_type || 'fire'}
              initialClaimAmount={caseData.total_demand || 0}
            />
          </Suspense>
        </section>

        {/* Evidence Checklist Section */}
        <section id="evidence" className="mt-8 pt-8 border-t border-slate-200">
          <h2 className="text-xl font-bold text-slate-900 mb-1">Evidence Checklist</h2>
          <p className="text-sm text-slate-500 mb-4">Track required evidence and documentation status</p>
          <Suspense fallback={<div className="animate-pulse bg-slate-100 h-64 rounded-xl" />}>
            <EvidenceChecklist
              caseId={caseId}
            />
          </Suspense>
        </section>

        {/* Demand Letter Section */}
        <section id="demand-letter" className="mt-8 pt-8 border-t border-slate-200">
          <h2 className="text-xl font-bold text-slate-900 mb-1">Demand Letter Generator</h2>
          <p className="text-sm text-slate-500 mb-4">Generate professional demand letters with state-specific statutory references</p>
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900">Create a Demand Letter</h3>
                <p className="text-sm text-slate-500">Auto-populated with case data. Supports initial demand, follow-up, bad faith notice, and final demand formats.</p>
              </div>
              <Link
                to={`/attorney/case/${caseId}/demand-letter`}
                className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                <FileText className="w-4 h-4" />
                Open Generator
              </Link>
            </div>
          </div>
        </section>

    </AttorneyLayout>
  );
}
